'use client'

import { useRef, useState, useCallback } from 'react'
import Link from 'next/link'
import { useInView } from '@/hooks/useInView'
import { Calendar, Zap, ArrowRight, Sparkles } from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaIframe } from '@/components/conversion/AttributedCtaIframe'

/* ── Cursor Spotlight Hook ────────────────────────────────── */
function useCursorSpotlight(glowColor: string) {
  const ref = useRef<HTMLDivElement>(null)
  const rectRef = useRef<DOMRect | null>(null)

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = rectRef.current
      if (!rect) return
      const el = ref.current
      if (!el) return
      el.style.setProperty('--spot-x', `${e.clientX - rect.left}px`)
      el.style.setProperty('--spot-y', `${e.clientY - rect.top}px`)
    },
    []
  )

  const handleMouseEnter = useCallback(() => {
    const el = ref.current
    if (!el) return
    // Cache rect on enter to avoid forced reflow on every mousemove
    rectRef.current = el.getBoundingClientRect()
    el.style.setProperty('--spot-opacity', '1')
  }, [])

  const handleMouseLeave = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.setProperty('--spot-opacity', '0')
    rectRef.current = null
  }, [])

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    pointerEvents: 'none',
    borderRadius: '1rem',
    background: `radial-gradient(400px circle at var(--spot-x, 0px) var(--spot-y, 0px), ${glowColor}, transparent 70%)`,
    opacity: 'var(--spot-opacity, 0)',
    transition: 'opacity 0.3s ease-out',
  }

  const overlay = <div aria-hidden style={overlayStyle} />

  return {
    containerProps: {
      ref,
      onMouseMove: handleMouseMove,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
    overlay,
  }
}

/* ── Animated beam lines ──────────────────────────────────── */
function AnimatedBeams() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="absolute h-[1px] bg-gradient-to-r from-transparent via-red-500/20 to-transparent"
          style={{
            top: `${20 + i * 15}%`,
            left: '-100%',
            width: '200%',
            animation: `beam-h ${8 + i * 2}s linear ${i * 1.5}s infinite`,
          }}
        />
      ))}
      {[...Array(3)].map((_, i) => (
        <div
          key={`v-${i}`}
          className="absolute w-[1px] bg-gradient-to-b from-transparent via-red-500/10 to-transparent"
          style={{
            left: `${25 + i * 25}%`,
            top: '-100%',
            height: '200%',
            animation: `beam-v ${12 + i * 3}s linear ${i * 2}s infinite`,
          }}
        />
      ))}

      {/* Keyframes for beam animations */}
      <style>{`
        @keyframes beam-h {
          from { transform: translateX(0); }
          to   { transform: translateX(50%); }
        }
        @keyframes beam-v {
          from { transform: translateY(0); }
          to   { transform: translateY(50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .absolute[style*="animation: beam"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  )
}

/* ── Demo Booking Modal (shared) ──────────────────────────── */
function DemoModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [loaded, setLoaded] = useState(false)

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Book a Demo"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-2xl glass-card-premium overflow-hidden z-10 transition-all duration-300 ${
          open
            ? 'scale-100 translate-y-0'
            : 'scale-95 translate-y-5'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800/50">
          <div>
            <h2 className="text-lg font-bold text-white">Book a Demo</h2>
            <p className="text-sm text-zinc-400">30-min live walkthrough</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-zinc-800/50 hover:bg-zinc-700/50 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="relative" style={{ minHeight: '600px' }}>
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-950">
              <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {open && (
            <AttributedCtaIframe
              src={BOOK_A_DEMO_URL}
              page="home"
              cta="CTA Section — Book a Demo"
              className="w-full border-0"
              style={{ height: '600px' }}
              onLoad={() => setLoaded(true)}
              title="Book a Demo Calendar"
            />
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Main CTA Section ─────────────────────────────────────── */
export function CTASection() {
  const { ref, isInView } = useInView()
  const [demoOpen, setDemoOpen] = useState(false)
  const viewCls = isInView ? 'in-view' : ''

  const bookDemoSpotlight = useCursorSpotlight('rgba(220, 38, 38, 0.18)')
  const pricingSpotlight = useCursorSpotlight('rgba(255, 255, 255, 0.08)')

  return (
    <>
      <section ref={ref} className="section relative overflow-hidden">
        <AnimatedBeams />

        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-600/[0.04] blur-[120px] rounded-full" />
        </div>

        <div className="section-inner relative z-10">
          {/* Header */}
          <div className={`anim-fade-in-up ${viewCls} text-center mb-12 sm:mb-16`}>
            <div
              className={`anim-scale-in anim-delay-1 ${viewCls} inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-zinc-800 bg-zinc-900/50 backdrop-blur-sm mb-6`}
            >
              <Sparkles size={14} className="text-red-500" />
              <span className="text-sm text-zinc-400">Two ways to start</span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Ready to Run Your Business on{' '}
              <span className="gradient-text-red">One System?</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Pick the path that fits how you like to evaluate software.
            </p>
          </div>

          {/* Two-card layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
            {/* Book a Demo — Primary */}
            <div
              className={`anim-fade-in-up anim-delay-2 ${viewCls} relative isolate overflow-hidden glass-card-premium p-8 border-glow-animated flex flex-col lg:scale-[1.02]`}
              {...bookDemoSpotlight.containerProps}
            >
              {bookDemoSpotlight.overlay}
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-600/15 rounded-xl mb-5">
                <Calendar className="text-red-500" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Book a Demo</h3>
              <div className="space-y-2 text-zinc-300 mb-6 flex-1">
                <p>
                  A 30-minute live walkthrough with our team. Bring your
                  questions, your workflow, your trade.
                </p>
                <p className="text-sm text-red-400 font-semibold">
                  Recommended · Fastest path to &ldquo;is this right for me?&rdquo;
                </p>
              </div>
              <button
                onClick={() => setDemoOpen(true)}
                className="w-full btn-primary text-base px-6 py-4 glow-red-sm group"
              >
                <span>Book a Demo</span>
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </div>

            {/* Pricing — Secondary */}
            <div
              className={`anim-fade-in-up anim-delay-3 ${viewCls} relative isolate overflow-hidden glass-card-premium p-8 flex flex-col`}
              {...pricingSpotlight.containerProps}
            >
              {pricingSpotlight.overlay}
              <div className="inline-flex items-center justify-center w-14 h-14 bg-zinc-800/60 rounded-xl mb-5">
                <Zap className="text-zinc-300" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                See Plans &amp; Pricing
              </h3>
              <div className="space-y-2 text-zinc-300 mb-6 flex-1">
                <p>
                  Flat monthly plans from $497 with unlimited users. No
                  contracts, cancel anytime.
                </p>
                <p className="text-sm text-zinc-500">
                  For builders who want the numbers before the walkthrough.
                </p>
              </div>
              <Link
                href="/pricing"
                className="w-full btn-ghost text-base px-6 py-4 justify-center group"
              >
                <span>View Pricing</span>
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          </div>

          {/* Bottom note */}
          <div className={`anim-fade-in anim-delay-6 ${viewCls} text-center`}>
            <p className="text-zinc-500 text-sm max-w-2xl mx-auto">
              Already a customer?{' '}
              <a
                href="https://app.builderlync.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-400 hover:text-red-300 underline underline-offset-2 transition-colors"
              >
                Sign in to your account
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  )
}
